import type { EditorAdapter } from './editor-adapter'

export interface ToolbarButton {
  name: string
  icon: string
  tooltip: string
  action: (editor: EditorAdapter) => void
  shortcut?: string
  shortcutMac?: string
  isActive?: (editor: EditorAdapter) => boolean
  dividerAfter?: boolean
}

export interface ToolbarGroup {
  name: string
  buttons: ToolbarButton[]
}

export const defaultToolbarGroups: ToolbarGroup[] = [
  {
    name: 'headings',
    buttons: [
      {
        name: 'heading1',
        icon: 'type',
        tooltip: 'Heading 1 (Ctrl+1)',
        shortcut: 'Ctrl+1',
        shortcutMac: 'Cmd+1',
        action: (editor) => editor.wrapLine('# ', ''),
      },
      {
        name: 'heading2',
        icon: 'type',
        tooltip: 'Heading 2 (Ctrl+2)',
        shortcut: 'Ctrl+2',
        shortcutMac: 'Cmd+2',
        action: (editor) => editor.wrapLine('## ', ''),
      },
      {
        name: 'heading3',
        icon: 'type',
        tooltip: 'Heading 3 (Ctrl+3)',
        shortcut: 'Ctrl+3',
        shortcutMac: 'Cmd+3',
        action: (editor) => editor.wrapLine('### ', ''),
      },
    ],
  },
  {
    name: 'formatting',
    buttons: [
      {
        name: 'bold',
        icon: 'bold',
        tooltip: 'Bold (Ctrl+B)',
        shortcut: 'Ctrl+B',
        shortcutMac: 'Cmd+B',
        action: (editor) => editor.wrapSelection('**', '**'),
        isActive: (editor) => editor.isSelectionWrapped('**', '**'),
      },
      {
        name: 'italic',
        icon: 'italic',
        tooltip: 'Italic (Ctrl+I)',
        shortcut: 'Ctrl+I',
        shortcutMac: 'Cmd+I',
        action: (editor) => editor.wrapSelection('*', '*'),
        isActive: (editor) => editor.isSelectionWrapped('*', '*'),
      },
      {
        name: 'strikethrough',
        icon: 'strikethrough',
        tooltip: 'Strikethrough (Ctrl+Shift+X)',
        shortcut: 'Ctrl+Shift+X',
        shortcutMac: 'Cmd+Shift+X',
        action: (editor) => editor.wrapSelection('~~', '~~'),
        isActive: (editor) => editor.isSelectionWrapped('~~', '~~'),
      },
      {
        name: 'code',
        icon: 'code',
        tooltip: 'Inline Code (Ctrl+`)',
        shortcut: 'Ctrl+`',
        shortcutMac: 'Cmd+`',
        action: (editor) => editor.wrapSelection('`', '`'),
        isActive: (editor) => editor.isSelectionWrapped('`', '`'),
      },
      {
        name: 'codeblock',
        icon: 'terminal',
        tooltip: 'Code Block (Ctrl+Shift+C)',
        shortcut: 'Ctrl+Shift+C',
        shortcutMac: 'Cmd+Shift+C',
        action: (editor) => editor.wrapSelection('\n```\n', '\n```\n'),
      },
    ],
  },
  {
    name: 'lists',
    buttons: [
      {
        name: 'bullet-list',
        icon: 'list',
        tooltip: 'Bullet List (Ctrl+Shift+8)',
        shortcut: 'Ctrl+Shift+8',
        shortcutMac: 'Cmd+Shift+8',
        action: (editor) => editor.toggleLinePrefix('- '),
      },
      {
        name: 'ordered-list',
        icon: 'list-ordered',
        tooltip: 'Ordered List (Ctrl+Shift+9)',
        shortcut: 'Ctrl+Shift+9',
        shortcutMac: 'Cmd+Shift+9',
        action: (editor) => editor.toggleLinePrefix('1. '),
      },
      {
        name: 'task-list',
        icon: 'check-square',
        tooltip: 'Task List (Ctrl+Shift+T)',
        shortcut: 'Ctrl+Shift+T',
        shortcutMac: 'Cmd+Shift+T',
        action: (editor) => editor.toggleLinePrefix('- [ ] '),
      },
      {
        name: 'indent',
        icon: 'indent',
        tooltip: 'Indent (Tab)',
        shortcut: 'Tab',
        action: (editor) => editor.indentLines(),
      },
      {
        name: 'outdent',
        icon: 'outdent',
        tooltip: 'Outdent (Shift+Tab)',
        shortcut: 'Shift+Tab',
        action: (editor) => editor.outdentLines(),
      },
    ],
  },
  {
    name: 'media',
    buttons: [
      {
        name: 'link',
        icon: 'link',
        tooltip: 'Link (Ctrl+K)',
        shortcut: 'Ctrl+K',
        shortcutMac: 'Cmd+K',
        action: (editor) => editor.insertLink(),
      },
      {
        name: 'image',
        icon: 'image',
        tooltip: 'Image (Ctrl+Shift+I)',
        shortcut: 'Ctrl+Shift+I',
        shortcutMac: 'Cmd+Shift+I',
        action: (editor) => editor.insertImage(),
      },
      {
        name: 'table',
        icon: 'table',
        tooltip: 'Table (Ctrl+T)',
        shortcut: 'Ctrl+T',
        shortcutMac: 'Cmd+T',
        action: (editor) => editor.insertTable(),
      },
    ],
  },
  {
    name: 'structure',
    buttons: [
      {
        name: 'quote',
        icon: 'quote',
        tooltip: 'Blockquote (Ctrl+Shift+Q)',
        shortcut: 'Ctrl+Shift+Q',
        shortcutMac: 'Cmd+Shift+Q',
        action: (editor) => editor.toggleLinePrefix('> '),
      },
      {
        name: 'hr',
        icon: 'minus',
        tooltip: 'Horizontal Rule (Ctrl+Shift+H)',
        shortcut: 'Ctrl+Shift+H',
        shortcutMac: 'Cmd+Shift+H',
        action: (editor) => editor.insertHorizontalRule(),
      },
      {
        name: 'footnote',
        icon: 'superscript',
        tooltip: 'Footnote',
        action: (editor) => editor.insertFootnote(),
      },
    ],
  },
  {
    name: 'math',
    buttons: [
      {
        name: 'math-inline',
        icon: 'square-root',
        tooltip: 'Inline Math ($$)',
        action: (editor) => editor.wrapSelection('$', '$'),
      },
      {
        name: 'math-block',
        icon: 'calculator',
        tooltip: 'Block Math ($$$)',
        action: (editor) => editor.wrapSelection('$$\n', '\n$$'),
      },
      {
        name: 'typst',
        icon: 'function-square',
        tooltip: 'Typst Block',
        action: (editor) => editor.wrapSelection('\n```typst\n', '\n```\n'),
      },
    ],
  },
  {
    name: 'actions',
    buttons: [
      {
        name: 'undo',
        icon: 'rotate-ccw',
        tooltip: 'Undo (Ctrl+Z)',
        shortcut: 'Ctrl+Z',
        shortcutMac: 'Cmd+Z',
        action: (editor) => editor.undo(),
      },
      {
        name: 'redo',
        icon: 'rotate-cw',
        tooltip: 'Redo (Ctrl+Y)',
        shortcut: 'Ctrl+Y',
        shortcutMac: 'Cmd+Y',
        action: (editor) => editor.redo(),
      },
    ],
  },
]

export function getIconComponent(iconName: string) {
  const icons: Record<string, string> = {
    type: 'Type',
    bold: 'Bold',
    italic: 'Italic',
    strikethrough: 'Strikethrough',
    code: 'Code',
    terminal: 'Terminal',
    list: 'List',
    'list-ordered': 'ListOrdered',
    'check-square': 'CheckSquare',
    indent: 'Indent',
    outdent: 'Outdent',
    link: 'Link',
    image: 'Image',
    table: 'Table',
    quote: 'Quote',
    minus: 'Minus',
    superscript: 'Superscript',
    'square-root': 'SquareRadical',
    calculator: 'Calculator',
    'function-square': 'FunctionSquare',
    'rotate-ccw': 'RotateCcw',
    'rotate-cw': 'RotateCw',
  }
  return icons[iconName] || 'HelpCircle'
}